/**
 * Phase J4.a — RFC 4180 CSV helpers for in-app downloads (Staff /
 * Parents / Students tabs). Pure functions; no DOM access on import so
 * Vitest + JSDOM can exercise them in isolation. `downloadCsv()` is
 * the only side-effectful entry — it constructs an in-memory Blob,
 * synthesises an anchor click, and revokes the object URL on the next
 * microtask so the file shows up in the user's Downloads bar without
 * leaving a leak behind.
 *
 * Why we hand-roll instead of pulling in `papaparse`:
 * - The export surface is intentionally narrow (a few thousand rows of
 *   already-typed data) so we don't need a streaming parser/encoder.
 * - The dependency footprint stays smaller — `papaparse` is ~45 kB
 *   minified + gzipped and we'd only use ~3% of its API.
 * - RFC 4180 escaping is short enough to be auditable in this file
 *   (`escapeCsvCell` is the only piece reviewers need to read).
 */

export type CsvCellValue = string | number | bigint | null | undefined;

/**
 * Characters that — when leading a cell — make Excel / Google Sheets /
 * LibreOffice interpret the cell as a formula on open. Phase D2.a
 * lifts the historical Phase J4.* over-claim about injection safety
 * from docs into code: any user-provided value starting with one of
 * these is prefixed with a TAB (per OWASP CSV-injection guidance —
 * https://owasp.org/www-community/attacks/CSV_Injection). The TAB
 * neutralises formula evaluation in spreadsheets while leaving the
 * payload otherwise intact for downstream CSV consumers.
 */
const FORMULA_INJECTION_LEADERS = /^[=+\-@\t\r]/;

/**
 * Prepend a TAB to any string cell whose first character could be
 * interpreted as a formula sentinel by a spreadsheet application.
 * Exported for the unit-test surface; called by `escapeCsvCell`.
 */
export function neutralizeFormulaCell(value: string): string {
  if (value.length === 0) return value;
  return FORMULA_INJECTION_LEADERS.test(value) ? `\t${value}` : value;
}

/**
 * Escape one cell per RFC 4180:
 *
 * - `null` / `undefined`         → empty string
 * - numeric `NaN` / `Infinity`   → empty string (defensive; produces a
 *                                   blank cell instead of the literal
 *                                   `NaN`/`Infinity` token which Excel
 *                                   refuses to round-trip)
 * - string with formula sentinel → prefixed with TAB to defuse Excel
 *                                   / Sheets formula evaluation on
 *                                   open (CSV-injection / CWE-1236)
 * - contains `,`, `"`, `\n`, `\r` → wrap in double quotes; double up
 *                                   any embedded `"` (RFC 4180 §2.7)
 * - all other strings/numbers    → cast to string as-is
 *
 * Returns the *cell body* only — the caller is responsible for
 * separators and line endings.
 *
 * Numeric inputs are passed through untouched: they cannot start with
 * a formula sentinel after `String(num)` because finite numbers begin
 * with a digit, `-`, or `.`. The `-` case is consciously excluded
 * from neutralisation for numbers (otherwise `-1` would render as
 * `\t-1`); the leader regex is only applied to string-typed cells.
 */
export function escapeCsvCell(value: CsvCellValue): string {
  if (value === null || value === undefined) return "";

  let stringValue: string;
  let neutraliseFormula = false;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    stringValue = String(value);
  } else if (typeof value === "bigint") {
    stringValue = value.toString();
  } else {
    stringValue = value;
    neutraliseFormula = true;
  }

  if (stringValue.length === 0) return "";

  if (neutraliseFormula) stringValue = neutralizeFormulaCell(stringValue);

  const mustQuote =
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r");

  if (!mustQuote) return stringValue;
  return `"${stringValue.replace(/"/g, '""')}"`;
}

/**
 * Build a complete CSV document from a header row + body rows. Uses
 * CRLF (`\r\n`) line endings per RFC 4180 §2.1; Excel and Google
 * Sheets both accept LF, but CRLF is the safer interop default. No
 * trailing line break — many parsers treat a trailing newline as an
 * empty row.
 */
export function buildCsv(
  headers: ReadonlyArray<string>,
  rows: ReadonlyArray<ReadonlyArray<CsvCellValue>>,
): string {
  const headerLine = headers.map(escapeCsvCell).join(",");
  const bodyLines = rows.map((row) => row.map(escapeCsvCell).join(","));
  if (bodyLines.length === 0) return headerLine;
  return [headerLine, ...bodyLines].join("\r\n");
}

/**
 * Trigger a CSV download in the user's browser. Idempotent across
 * repeat clicks — each call mints a fresh `Blob` + anchor. The MIME
 * type is `text/csv;charset=utf-8` and we prepend a UTF-8 BOM so
 * Excel on Windows opens non-ASCII cells (names with accents, Indic
 * scripts, etc.) without garbling.
 *
 * Filename validation — strip filesystem-hostile characters but
 * preserve dots, hyphens, and underscores so callers can pass
 * `staff-acme-2026-05-15.csv` literally. Spaces are replaced with
 * hyphens for the same reason.
 */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === "undefined" || typeof URL === "undefined") {
    // Server-render guard — Vitest's JSDOM provides both, but a future
    // SSR path shouldn't crash on import.
    return;
  }
  const safeName = sanitizeCsvFilename(filename);
  const utf8Bom = "\uFEFF";
  const blob = new Blob([utf8Bom + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safeName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Microtask revoke — browsers need the URL alive long enough for the
  // download to start, but we don't want to leak it past that tick.
  Promise.resolve().then(() => URL.revokeObjectURL(url));
}

/**
 * Strip filesystem-hostile characters from a proposed CSV filename.
 * Exported for the unit-test surface; not part of the public API for
 * page components.
 */
export function sanitizeCsvFilename(filename: string): string {
  const trimmed = filename.trim();
  const fallback = "export.csv";
  if (trimmed.length === 0) return fallback;
  const cleaned = trimmed
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (cleaned.length === 0) return fallback;
  return cleaned.endsWith(".csv") ? cleaned : `${cleaned}.csv`;
}

/**
 * Build the canonical `<surface>-<tenant_slug>-<YYYY-MM-DD>.csv`
 * filename used by every roster export. Exported so each tab's hook
 * doesn't have to re-derive the date format.
 */
export function buildRosterCsvFilename(
  surface: string,
  tenantSlug: string | null | undefined,
  now: Date = new Date(),
): string {
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  const date = `${yyyy}-${mm}-${dd}`;
  const slug = (tenantSlug ?? "").trim() || "tenant";
  return sanitizeCsvFilename(`${surface}-${slug}-${date}.csv`);
}
